const React = require('react');
const { useEffect, useState } = React;
const { Card, Row, Col, Typography, Timeline } = require('antd');
const { 
  UserOutlined, 
  CarOutlined, 
  DollarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  RocketOutlined,
  BarChartOutlined,
  LineChartOutlined,
  ThunderboltOutlined
} = require('@ant-design/icons');
const { useAuth } = require('../../contexts/AuthContext');

const { Title, Text } = Typography;

function SuperAdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    totalClients: 0,
    totalLocations: 0,
    totalVehicles: 0,
    monthlyRevenue: 0,
    activeCampaigns: 0,
    activeMembers: 0
  });
  const [metrics, setMetrics] = useState({
    campaignCTR: 0,
    revenuePerClient: 0,
    totalImpressions: 0,
    efficiencyScore: 0
  });
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const qs = 'limit=5000&offset=0';
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [cRes, wRes, campRes, uRes, logsRes, geotabRes] = await Promise.all([
          fetch(`/api/clients?${qs}`, { headers }),
          fetch(`/api/workspaces?${qs}`, { headers }),
          fetch(`/api/campaigns?${qs}`, { headers }),
          fetch(`/api/users?${qs}`, { headers }),
          fetch(`/api/logs?limit=10&offset=0`, { headers }),
          fetch(`/api/integrations/geotab/devices/count?all=true`, { headers }),
        ]);
        
        const [clients, workspaces, campaigns, users, logs, geotabData] = await Promise.all([
          cRes.ok ? cRes.json() : Promise.resolve({ items: [] }),
          wRes.ok ? wRes.json() : Promise.resolve({ items: [] }),
          campRes.ok ? campRes.json() : Promise.resolve({ items: [] }),
          uRes.ok ? uRes.json() : Promise.resolve({ items: [] }),
          logsRes.ok ? logsRes.json() : Promise.resolve({ items: [] }),
          geotabRes.ok ? geotabRes.json() : Promise.resolve({ totalDevices: 0 }),
        ]);
        
        if (!mounted) return;
        
        const clientsCount = Array.isArray(clients.items) ? clients.items.length : 0;
        const locationsCount = Array.isArray(workspaces.items) ? workspaces.items.length : 0;
        const vehiclesCount = geotabData.totalDevices || 0;
        const campaignsCount = Array.isArray(campaigns.items) ? campaigns.items.filter(c => c.status === 'active').length : 0;
        const membersCount = Array.isArray(users.items) ? users.items.length : 0;
        
        // Calculate monthly revenue (mock calculation)
        const monthlyRev = clientsCount * 150; // $150 avg per client
        
        setStats({
          totalClients: clientsCount,
          totalLocations: locationsCount,
          totalVehicles: vehiclesCount,
          monthlyRevenue: monthlyRev,
          activeCampaigns: campaignsCount,
          activeMembers: membersCount
        });
        
        // Calculate performance metrics
        setMetrics({
          campaignCTR: 3.86,
          revenuePerClient: monthlyRev / (clientsCount || 1),
          totalImpressions: vehiclesCount * 1200,
          efficiencyScore: 38
        });
        
        // Format activity logs
        const formattedActivities = (Array.isArray(logs.items) ? logs.items : []).slice(0, 6).map(log => ({
          title: log.action || 'Activity',
          description: log.details || log.message || '',
          timestamp: log.created_at || new Date().toISOString()
        }));
        setActivities(formattedActivities);
        
      } catch (err) {
        console.error('Dashboard load error:', err);
      }
    }
    load();
    return () => { mounted = false; };
  }, [token]);

  const platformCards = [
    { title: 'Total Clients', value: stats.totalClients, color: '#3b82f6', icon: React.createElement(UserOutlined), subtitle: 'All registered clients' },
    { title: 'Total Locations', value: stats.totalLocations, color: '#8b5cf6', icon: React.createElement(EnvironmentOutlined), subtitle: 'Active workspaces' },
    { title: 'Total Vehicles', value: stats.totalVehicles, color: '#a855f7', icon: React.createElement(CarOutlined), subtitle: 'Fleet vehicles' },
    { title: 'Monthly Revenue', value: `$${stats.monthlyRevenue.toLocaleString()}`, color: '#10b981', icon: React.createElement(DollarOutlined), subtitle: 'Current month' },
    { title: 'Active Campaigns', value: stats.activeCampaigns, color: '#f97316', icon: React.createElement(RocketOutlined), subtitle: 'Running campaigns' },
    { title: 'Active Members', value: stats.activeMembers, color: '#06b6d4', icon: React.createElement(TeamOutlined), subtitle: 'Platform users' },
  ];
  
  const performanceMetrics = [
    { title: 'Campaign CTR', value: `${metrics.campaignCTR}%`, change: '+0.3% from last week', trend: 'up', icon: React.createElement(BarChartOutlined), color: '#3b82f6' },
    { title: 'Revenue/Client', value: `$${metrics.revenuePerClient.toFixed(2)}`, change: 'Monthly $42.18', trend: 'up', icon: React.createElement(DollarOutlined), color: '#10b981' },
    { title: 'Total Impressions', value: `${(metrics.totalImpressions / 1000).toFixed(1)}K`, change: '1.4M all time', trend: 'neutral', icon: React.createElement(LineChartOutlined), color: '#8b5cf6' },
    { title: 'Efficiency Score', value: metrics.efficiencyScore, change: 'Efficiency score', trend: 'neutral', icon: React.createElement(ThunderboltOutlined), color: '#f97316' },
  ];
  
  const channelData = [
    { name: 'SMS', platform: 'Twilio', spend: '$7.1K', impressions: '51.9K', ctr: '4.02%', color: '#ec4899' },
    { name: 'Email', platform: 'SendGrid', spend: '$5.2K', impressions: '41.3K', ctr: '1.58%', color: '#3b82f6' },
    { name: 'Meta', platform: 'Facebook', spend: '$10.4K', impressions: '87.6K', ctr: '2.21%', color: '#8b5cf6' },
    { name: 'Google', platform: 'Google Ads', spend: '$12.8K', impressions: '104.2K', ctr: '3.1%', color: '#f97316' },
  ];



  return React.createElement('div', { style: { padding: '24px', background: '#f5f5f5', minHeight: '100vh' } },
    // Header
    React.createElement('div', { style: { marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
      React.createElement(Title, { level: 3, style: { margin: 0 } }, 'Platform Overview'),
      React.createElement(Text, { type: 'secondary' }, `Last 30 days`)
    ),
    
    // Platform Overview Cards
    React.createElement(Row, { gutter: [16, 16], style: { marginBottom: '24px' } },
      platformCards.map((card, idx) => 
        React.createElement(Col, { xs: 24, sm: 12, md: 8, lg: 4, key: idx },
          React.createElement(Card, { 
            style: { 
              background: card.color,
              color: 'white',
              borderRadius: '8px',
              border: 'none'
            },
            bodyStyle: { padding: '20px' }
          },
            React.createElement('div', { style: { fontSize: '24px', marginBottom: '8px' } }, card.icon),
            React.createElement('div', { style: { fontSize: '28px', fontWeight: 'bold', marginBottom: '4px' } }, card.value),
            React.createElement('div', { style: { fontSize: '14px', opacity: 0.9 } }, card.title),
            React.createElement('div', { style: { fontSize: '12px', opacity: 0.7, marginTop: '4px' } }, card.subtitle)
          )
        )
      )
    ),
    
    // Performance Metrics
    React.createElement('div', { style: { marginBottom: '16px' } },
      React.createElement(Title, { level: 4, style: { margin: 0 } }, 'Performance Metrics')
    ),
    React.createElement(Row, { gutter: [16, 16], style: { marginBottom: '24px' } },
      performanceMetrics.map((metric, idx) => 
        React.createElement(Col, { xs: 24, sm: 12, md: 6, key: idx },
          React.createElement(Card, { style: { borderRadius: '8px' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', marginBottom: '12px' } },
              React.createElement('div', { style: { fontSize: '24px', color: metric.color, marginRight: '12px' } }, metric.icon),
              React.createElement('div', null,
                React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, metric.title),
                React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold' } }, metric.value)
              )
            ),
            React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, metric.change)
          )
        )
      )
    ),
    
    // Channel Analytics
    React.createElement('div', { style: { marginBottom: '16px' } },
      React.createElement(Title, { level: 4, style: { margin: 0 } }, 'Channel Analytics')
    ),
    React.createElement(Row, { gutter: [16, 16], style: { marginBottom: '24px' } },
      channelData.map((channel, idx) => 
        React.createElement(Col, { xs: 24, sm: 12, md: 6, key: idx },
          React.createElement(Card, { style: { borderRadius: '8px' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', marginBottom: '12px' } },
              React.createElement('div', { style: { width: '40px', height: '40px', borderRadius: '50%', background: channel.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px', marginRight: '12px' } }, channel.name.charAt(0)),
              React.createElement('div', null,
                React.createElement('div', { style: { fontWeight: 'bold' } }, channel.name),
                React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, channel.platform)
              )
            ),
            React.createElement('div', { style: { marginBottom: '8px' } },
              React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'Spend'),
              React.createElement('div', { style: { fontSize: '18px', fontWeight: 'bold' } }, channel.spend)
            ),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between' } },
              React.createElement('div', null,
                React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'Impressions'),
                React.createElement('div', { style: { fontSize: '14px', fontWeight: '600' } }, channel.impressions)
              ),
              React.createElement('div', null,
                React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'CTR'),
                React.createElement('div', { style: { fontSize: '14px', fontWeight: '600' } }, channel.ctr)
              )
            )
          )
        )
      )
    ),
    
    // Recent Platform Activity
    React.createElement('div', { style: { marginBottom: '16px' } },
      React.createElement(Title, { level: 4, style: { margin: 0 } }, 'Recent Platform Activity')
    ),
    React.createElement(Card, { style: { borderRadius: '8px' } },
      React.createElement(Timeline, { style: { marginTop: '16px' } },
        activities.length > 0 ? activities.map((activity, idx) => 
          React.createElement(Timeline.Item, { key: idx },
            React.createElement('div', { style: { fontWeight: '600', marginBottom: '4px' } }, activity.title),
            React.createElement('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '4px' } }, activity.description),
            React.createElement('div', { style: { fontSize: '11px', color: '#9ca3af' } }, new Date(activity.timestamp).toLocaleString())
          )
        ) : [
          React.createElement(Timeline.Item, { key: 1 },
            React.createElement('div', { style: { fontWeight: '600' } }, 'Campaign launched'),
            React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'New campaign "Summer Promo" started by Agency A'),
            React.createElement('div', { style: { fontSize: '11px', color: '#9ca3af' } }, '2 hours ago')
          ),
          React.createElement(Timeline.Item, { key: 2 },
            React.createElement('div', { style: { fontWeight: '600' } }, 'New vehicle added'),
            React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'Fleet vehicle #1245 added to Client B'),
            React.createElement('div', { style: { fontSize: '11px', color: '#9ca3af' } }, '4 hours ago')
          ),
          React.createElement(Timeline.Item, { key: 3 },
            React.createElement('div', { style: { fontWeight: '600' } }, 'Monthly campaign posted'),
            React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'Recurring campaign for Client C posted'),
            React.createElement('div', { style: { fontSize: '11px', color: '#9ca3af' } }, '1 day ago')
          ),
          React.createElement(Timeline.Item, { key: 4 },
            React.createElement('div', { style: { fontWeight: '600' } }, 'Location added'),
            React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'New workspace "Downtown Office" created'),
            React.createElement('div', { style: { fontSize: '11px', color: '#9ca3af' } }, '2 days ago')
          ),
          React.createElement(Timeline.Item, { key: 5 },
            React.createElement('div', { style: { fontWeight: '600' } }, 'Campaign completed'),
            React.createElement('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'Campaign "Spring Sale" completed with 3.2% CTR'),
            React.createElement('div', { style: { fontSize: '11px', color: '#9ca3af' } }, '5 days ago')
          )
        ]
      )
    )
  );
}

export default SuperAdminDashboard;
